import { merge } from 'lodash-es';
import * as yup from 'yup';
import { IViewDataModel } from '../data-models/view.data-model';
import { type PageContext } from '../navigation/usePageContext';

const ChatMessageRoles = {
  user: 'user',
  assistant: 'assistant',
  system: 'system',
} as const;
export type ChatMessageRoles = (typeof ChatMessageRoles)[keyof typeof ChatMessageRoles];

// REST API
export const MessageFeedback = {
  up: 'up',
  down: 'down',
} as const;
export type MessageFeedback = (typeof MessageFeedback)[keyof typeof MessageFeedback] | null;

export const agentMessageSchema = yup.object({
  content: yup.string().required(),
  conversation_id: yup.number().required(),
  feedback: yup.string().oneOf(Object.values(MessageFeedback)).nullable().default(null),
  id: yup.number().required(),
  role: yup.string().oneOf(Object.values(ChatMessageRoles)).required(),
  // Optional open metadata bag; carries the out-of-band marker
  // (`additional_kwargs.source`) on event-turn replies and legacy Atlas
  // digests (FD-4182). Present on WS complete frames; the REST history
  // endpoint does not expose it yet.
  additional_kwargs: yup.mixed<AgentMessageAdditionalKwargs>(),
});

/** What the screen says with a message: the page the user is on, in the page catalog's terms,
 *  with what they have selected there. (Older builds sent the selected recon run flat; the
 *  agent still reads that form.) */
export type ChatMessageContext = { page: PageContext };

export const agentNewChatMessageSchema = yup.object({
  content: yup.string().required(),
  conversation_id: yup.number(),
  // What the user is looking at when they sent the message — rides alongside
  // the message so the agent can resolve "this run" or "this company" without
  // the user naming an id. Not part of the persisted MessageData.
  context: yup.mixed<ChatMessageContext>().optional(),
  // The version of the page catalog this build speaks. Present, the agent
  // links pages (`app:` links) and sends `navigate` frames; absent, it
  // behaves as it did before pages had names.
  nav_catalog: yup.string().optional(),
});

export const agentConversationSchema = yup.object({
  id: yup.number().required(),
  timestamp: yup.string(),
  title: yup.string().nullable(),
  user_id: yup.string().required(),
});

export const createConversationRequestSchema = yup.object({
  title: yup.string().optional(),
});

/** POST /conversations/{id}/cancel. With no active generation the agent
 *  answers `{ cancelled: false, message }` and none of the other fields. */
export const agentQueryCancelledResponseSchema = yup.object({
  cancelled: yup.boolean().required(),
  message: yup.string(),
  message_id: yup.number(),
  partial_content: yup.string(),
  tokens_used: yup.number(),
});

// Socket API
export const ChatSocketMessageType = {
  agentActivity: 'agent_activity',
  aiResponseChunk: 'ai_response_chunk',
  aiResponseComplete: 'ai_response_complete',
  aiResponseStart: 'ai_response_start',
  assistantAttention: 'assistant_attention',
  connectionEstablished: 'connection_established',
  chat: 'chat',
  error: 'error',
  generationCanceled: 'generation_canceled',
  navigate: 'navigate',
  titleUpdated: 'title_updated',
  viewSaved: 'view_saved',
} as const;
export type ChatSocketMessageType = (typeof ChatSocketMessageType)[keyof typeof ChatSocketMessageType];

export const socketConnectedResponseSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.connectionEstablished]).required(),
  conversations: yup.array().of(agentConversationSchema),
  user: yup.object({
    id: yup.string().required(),
    email: yup.string().email().required(),
    full_name: yup.string().required(),
    // Optional: older agent deployments don't include it; the tenant-mismatch
    // banner stays dormant without it.
    tenant_id: yup.string(),
  }),
});

export const socketNewConversationMessageSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.chat]).required(),
  content: yup.string().required(),
  conversation_id: yup.number(),
});

/** A generation turn began: `message` is the placeholder assistant row the
 *  stream renders into; its `id` is stable for the whole stream. Older agent
 *  deployments never send this frame — the chunk/complete fallbacks cover it. */
export const socketAiResponseStartSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.aiResponseStart]).required(),
  message: yup.object({
    id: yup.number().required(),
    conversation_id: yup.number().required(),
    role: yup.string().oneOf([ChatMessageRoles.assistant]).required(),
    status: yup.string(),
  }),
});

export const socketMessageResponseChunkSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.aiResponseChunk]).required(),
  chunk: yup.string().required(),
  conversation_id: yup.number(),
  // Optional: the placeholder row id the stream renders into. Older agent
  // deployments omit it and the FE falls back to the last-bubble heuristic.
  message_id: yup.number(),
});

/** Open metadata bag on assistant messages; `source` names the producing
 *  background agent for out-of-band pushes (e.g. 'atlas' digests). Declared as
 *  a plain interface (via `yup.mixed`) so the field stays optional in the
 *  inferred type — nested `yup.object`s infer as required. */
export interface AgentMessageAdditionalKwargs {
  source?: string;
}

export const socketMessageResponseCompleteSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.aiResponseComplete]).required(),
  message: agentMessageSchema.shape({
    role: yup.string().oneOf([ChatMessageRoles.assistant]).required(),
    // Optional: the placeholder id the stream was rendered under. May differ
    // from the final `id` — the agent deletes the placeholder and persists
    // fresh rows on finalize. Match bubbles on this, then adopt the new `id`.
    replaces_message_id: yup.number(),
    // `additional_kwargs` (from the base schema) is stamped with
    // `source: 'atlas'` on event-turn replies; legacy digest frames carried it
    // with no preceding start/chunk frames.
    // The `app:` link targets the agent's link check rejected after they
    // streamed; the saved reply has them as labels, and the bubble follows.
    // Absent when nothing was rejected.
    unlinked: yup.array().of(yup.string().required()),
  }),
});

export const socketConversationTitleUpdatedSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.titleUpdated]).required(),
  conversation_id: yup.number().required(),
  title: yup.string().required(),
});

/** generation successfully cancelled */
export const socketGenerationCancelledSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.generationCanceled]).required(),
  conversation_id: yup.number().required(),
  message_id: yup.number().required(),
  partial_content: yup.string(),
  tokens_used: yup.number(),
});

/** noop message after receiving a cancel message */
export const socketNoActiveGenerationToCancelSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.generationCanceled]).required(),
  conversation_id: yup.number().required(),
  message: yup.string().default('No active generation to cancel'),
});

export const socketErrorResponseSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.error]).required(),
  message: yup.string().required().default('An error occurred'),
  // Present when the error belongs to one conversation's turn; absent for
  // connection-level errors
  conversation_id: yup.number(),
});

export const AgentActivityScope = {
  /** Activity bound to the in-flight generation turn — implicitly cleared when
   *  the turn completes or is cancelled. */
  turn: 'turn',
  /** Free-running background-agent activity (e.g. Atlas mid-engagement) —
   *  persists across turns until the producer clears it with `label: null`. */
  ambient: 'ambient',
} as const;
export type AgentActivityScope = (typeof AgentActivityScope)[keyof typeof AgentActivityScope];

/** Ephemeral "what's happening right now" frame. Last-write-wins per
 *  `(scope, agent)`; never persisted. `agent` is an open string naming the
 *  acting agent ("atlas", ...) and is omitted for the chat agent itself.
 *  `label: null` clears the entry. */
export const socketAgentActivitySchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.agentActivity]).required(),
  conversation_id: yup.number().nullable(),
  scope: yup.string().oneOf(Object.values(AgentActivityScope)).required(),
  agent: yup.string().nullable(),
  label: yup.string().nullable().defined(),
  tool: yup.string().nullable(),
  ts: yup.string().nullable(),
});

export const AssistantAttentionReason = {
  /** A question, an escalation, or an approval waiting. */
  needsInput: 'needs_input',
  /** Finished, paused, or failed. */
  update: 'update',
} as const;
export type AssistantAttentionReason =
  (typeof AssistantAttentionReason)[keyof typeof AssistantAttentionReason];

/** An Atlas event turn's reply landed in a conversation, which the user may not be looking at.
 *  Sent to every socket the user has open; a tab acting on another tenant ignores it. */
export const socketAssistantAttentionSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.assistantAttention]).required(),
  conversation_id: yup.number().required(),
  tenant_id: yup.string().required(),
  source: yup.string(),
  reason: yup.string().oneOf(Object.values(AssistantAttentionReason)).required(),
  title: yup.string().nullable(),
});

/** The user asked to be taken to a page and the agent's `navigate` tool ran. Sent mid-turn to
 *  every socket the user has open; only the tab showing the conversation acts on it, and the
 *  reply links the same page for every other tab and for history. The agent validated the
 *  destination against the catalog; the front end resolves it again. */
export const socketNavigateSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.navigate]).required(),
  conversation_id: yup.number().nullable(),
  destination: yup.string().required(),
  params: yup.mixed<Record<string, unknown>>(),
  label: yup.string(),
});

/** The agent created or changed a saved view (`view_save`, `view_save_mpr`). `view` is the
 *  api's own row from POST /view or PUT /view/:id, the shape `deserializeView` reads. Sent to
 *  every socket the user has open; a tab acting on another tenant ignores it. */
export const socketViewSavedSchema = yup.object({
  type: yup.string().oneOf([ChatSocketMessageType.viewSaved]).required(),
  conversation_id: yup.number().nullable(),
  tenant_id: yup.string().required(),
  saved: yup.string().oneOf(['created', 'updated']).required(),
  view: yup.mixed<IViewDataModel>().required(),
});

export type IAgentMessageDataModel = yup.InferType<typeof agentMessageSchema>;
export type IAgentConversationDataModel = yup.InferType<typeof agentConversationSchema>;
export type ICreateConversationRequest = yup.InferType<typeof createConversationRequestSchema>;
export type AgentQueryCancelledResponse = yup.InferType<typeof agentQueryCancelledResponseSchema>;
export type ISocketConnectedResponse = yup.InferType<typeof socketConnectedResponseSchema> & {
  type: typeof ChatSocketMessageType.connectionEstablished;
};
export type SocketNewConversationMessage = yup.InferType<typeof socketNewConversationMessageSchema> & {
  type: typeof ChatSocketMessageType.chat;
};
export type SocketAiResponseStart = yup.InferType<typeof socketAiResponseStartSchema> & {
  type: typeof ChatSocketMessageType.aiResponseStart;
};
export type SocketMessageResponseChunk = yup.InferType<typeof socketMessageResponseChunkSchema> & {
  type: typeof ChatSocketMessageType.aiResponseChunk;
};
export type SocketMessageResponseComplete = yup.InferType<typeof socketMessageResponseCompleteSchema> & {
  type: typeof ChatSocketMessageType.aiResponseComplete;
};
export type IChatMessage = yup.InferType<typeof agentNewChatMessageSchema> & {
  type: typeof ChatSocketMessageType.chat;
};

export type SocketConversationTitleUpdated = yup.InferType<typeof socketConversationTitleUpdatedSchema> & {
  type: typeof ChatSocketMessageType.titleUpdated;
};

export type SocketGenerationCancelled = yup.InferType<typeof socketGenerationCancelledSchema> & {
  type: typeof ChatSocketMessageType.generationCanceled;
};
export type SocketNoActiveGenerationToCancel = yup.InferType<
  typeof socketNoActiveGenerationToCancelSchema
> & {
  type: typeof ChatSocketMessageType.generationCanceled;
};
export type SocketErrorResponse = yup.InferType<typeof socketErrorResponseSchema> & {
  type: typeof ChatSocketMessageType.error;
};
export type SocketAgentActivity = yup.InferType<typeof socketAgentActivitySchema> & {
  type: typeof ChatSocketMessageType.agentActivity;
};
export type SocketViewSaved = yup.InferType<typeof socketViewSavedSchema> & {
  type: typeof ChatSocketMessageType.viewSaved;
};
export type SocketNavigate = yup.InferType<typeof socketNavigateSchema> & {
  type: typeof ChatSocketMessageType.navigate;
};
export type SocketAssistantAttention = yup.InferType<typeof socketAssistantAttentionSchema> & {
  type: typeof ChatSocketMessageType.assistantAttention;
};

export function createAgentConversationDataModel(
  overrides: Partial<IAgentConversationDataModel> = {}
): IAgentConversationDataModel {
  return merge({}, agentConversationSchema.getDefault(), {
    id: 1,
    title: 'Test Conversation',
    user_id: 'test-user-id',
    ...overrides,
  });
}

export function createAgentMessageDataModel(
  overrides: Partial<IAgentMessageDataModel> = {}
): IAgentMessageDataModel {
  return merge({}, agentMessageSchema.getDefault(), {
    id: 1,
    content: 'Test Message',
    conversation_id: 1,
    role: ChatMessageRoles.user,
    ...overrides,
  });
}
